import { useState, useMemo, useEffect, useRef } from "react";
import TaskCard from "./TaskCard";
import { getIcon, PLATFORMS } from "../../constants/constants";
import { getPlatformColumns } from "../../utils/platformConfigFields";
import { getTaskOptions } from "../../features/tasks/taskSlice";
import { useDispatch, useSelector } from "react-redux";

const groupTasksByPlatform = (actions = [], settings = []) => {
  const grouped = {};
  actions.forEach((action) => {
    const setting = settings.find((s) => s.uuid === action.settingId);
    const platformType = setting?.assistantId?.platform?.type || "unknown";
    const platformName = setting?.assistantId?.platform?.name || platformType;
    if (!grouped[platformType]) {
      grouped[platformType] = { platformName: platformName, actions: [] };
    }
    grouped[platformType].actions.push(action);
  });
  return grouped;
};

const TaskList = ({
  actions = [],
  groupByPlatform = true,
  selectedTasks,
  editingTaskId,
  editedTaskName,
  editedTaskDescription,
  updatingTaskId,
  meeting,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDeleteTask,
  onSend,
  onKeyDown,
  onTaskNameChange,
  onDescriptionChange,
  settings,
  taskSettingMap,
  onToggleTask,
  onTogglePlatformTask,
  handleToggleAll,
  participants,
  executingTaskIds,
  onTaskUpdate,
}) => {
  const groupedTasks = useMemo(
    () => groupTasksByPlatform(actions, settings),
    [actions, settings],
  );

  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    const grouped = groupTasksByPlatform(actions, settings);
    Object.keys(grouped).forEach((name) => {
      initial[name] = true;
    });
    return initial;
  });

  const dispatch = useDispatch();
  const { taskOptions } = useSelector((state) => state.task);
  const fetchedKeysRef = useRef(new Set());

  useEffect(() => {
    actions.forEach((action) => {
      const setting = settings.find((s) => s.uuid === action.settingId);
      const platformType = setting?.assistantId?.platform?.type;

      if (
        action.settingId &&
        platformType &&
        platformType !== PLATFORMS.GOOGLE_CALENDAR &&
        platformType !== PLATFORMS.OUTLOOK_CALENDAR &&
        platformType !== "unknown"
      ) {
        const key = `${platformType}_${action.settingId}`;
        if (!fetchedKeysRef.current.has(key)) {
          fetchedKeysRef.current.add(key);
          dispatch(
            getTaskOptions({
              settingId: action.settingId,
              platformType,
              meetingId: meeting?.uuid,
            }),
          );
        }
      }
    });
  }, [actions, settings, dispatch, meeting?.uuid]);

  const togglePlatform = (name) =>
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));

  const getPlatformIconUrl = (platformType) => {
    const setting = settings.find(
      (s) => s?.assistantId?.platform?.type === platformType,
    );
    return (
      getIcon(setting?.assistantId?.platform?.type) ||
      setting?.assistantId?.platform?.iconUrl ||
      null
    );
  };

  if (actions.length === 0) {
    return <div className="text-center py-4">No actions found</div>;
  }

  return (
    <div className="d-flex flex-column gap-1">
      {Object.entries(groupedTasks).map(
        ([platformType, { platformName, actions: platformActions }]) => {
          const completedCount = platformActions.filter(
            (t) => t.status === "completed",
          ).length;
          const allSelected =
            platformActions.length > 0 &&
            platformActions.every((t) => selectedTasks.includes(t.uuid));
          const iconUrl = getPlatformIconUrl(platformType);
          const isOpen = (expanded[platformType] ?? true) || allSelected;

          const hasResult = platformActions.some(
            (t) => t?.actionLogId?.status === "success",
          );

          const hasFailure = platformActions.some(
            (t) => t?.actionLogId?.status === "failure",
          );
          const columns = getPlatformColumns({
            platformType,
            hasResult: hasResult,
            hasFailure: hasFailure,
          });

          const totalCols = columns.length;

          return (
            <div key={platformType} className="border rounded">
              <div>
                <table
                  className="table table-sm align-middle mb-0"
                  style={{
                    tableLayout: "auto",
                    width: "100%",
                  }}
                >
                  <thead>
                    <tr
                      onClick={() => togglePlatform(platformType)}
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <th colSpan={totalCols} style={{ padding: "8px 12px" }}>
                        <div className="d-flex align-items-center justify-content-between w-100">
                          <div className="d-flex align-items-center gap-2">
                            <i
                              className={`bi ${isOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
                              style={{ fontSize: "12px" }}
                            />
                            {iconUrl ? (
                              <img
                                src={iconUrl}
                                alt={platformName}
                                width={16}
                                height={16}
                              />
                            ) : (
                              <i className="bi bi-box text-muted" />
                            )}
                            <span className="fw-semibold">{platformName}</span>
                          </div>
                          <div
                            className="d-flex align-items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="small text-muted">
                              {completedCount}/{platformActions.length} done
                            </span>
                          </div>
                        </div>
                      </th>
                    </tr>

                    {isOpen && (
                      <tr style={{ fontSize: "11px" }}>
                        {columns.map((col) => (
                          <th key={col.key}>{col.label}</th>
                        ))}
                      </tr>
                    )}
                  </thead>

                  {isOpen && (
                    <tbody>
                      {platformActions.map((action, taskIndex) => {
                        const key = `${platformType}_${action.settingId}`;

                        const matchedParticipants =
                          taskOptions?.[key]?.matchedParticipants || [];

                        return (
                          <TaskCard
                            key={action.uuid}
                            action={action}
                            serialNo={taskIndex + 1}
                            selectedTasks={selectedTasks}
                            editingTaskId={editingTaskId}
                            editedTaskName={editedTaskName}
                            editedTaskDescription={editedTaskDescription}
                            updatingTaskId={updatingTaskId}
                            meeting={meeting}
                            onStartEdit={onStartEdit}
                            onCancelEdit={onCancelEdit}
                            onSaveEdit={onSaveEdit}
                            onDeleteTask={onDeleteTask}
                            onSend={onSend}
                            onKeyDown={onKeyDown}
                            onTaskNameChange={onTaskNameChange}
                            onDescriptionChange={onDescriptionChange}
                            settings={settings}
                            taskSettingMap={taskSettingMap}
                            onToggleTask={onToggleTask}
                            participants={
                              matchedParticipants.length > 0
                                ? matchedParticipants
                                : participants
                            }
                            executingTaskIds={executingTaskIds}
                            onTaskUpdate={onTaskUpdate}
                            columns={columns}
                            platformType={platformType}
                            showResponse={hasResult}
                            showError={hasFailure}
                          />
                        );
                      })}
                    </tbody>
                  )}
                </table>
              </div>
            </div>
          );
        },
      )}
    </div>
  );
};

export default TaskList;
